import { Copy, Plus, Archive, ArrowUp, ArrowDown, Play } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/feedback";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { TreeSummary } from "@/lib/queries-intake-trees";

/**
 * The trees a tenant keeps, above the canvas (ACL-340).
 *
 * One tab per tree, with the one that is running marked. A tenant runs one at a time and every
 * lead walks it, so the others are what an admin is building or keeping.
 *
 * Each tab says what the tree holds — a published version, a draft, or neither — because that is
 * what decides whether it can be run at all.
 */
export function TreeBar({
  trees,
  warnings,
  status,
  selected,
  readOnly,
  onSelect,
  onAdd,
  onDuplicate,
  onArchive,
  onMove,
  onActivate,
}: {
  trees: TreeSummary[];
  warnings: string[];
  /** Which version is on screen. It belongs with the tree it is a version of, above the
      warnings, so two callouts are never split apart by a status line. */
  status?: React.ReactNode;
  selected: number | null;
  readOnly: boolean;
  onSelect: (treeId: number) => void;
  onAdd: () => void;
  onDuplicate: (tree: TreeSummary) => void;
  onArchive: (tree: TreeSummary) => void;
  onMove: (tree: TreeSummary, direction: -1 | 1) => void;
  /** Make this the one running tree, or stand it down. */
  onActivate: (tree: TreeSummary, active: boolean) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {trees.map((tree, index) => {
          const active = tree.id === selected;
          return (
            <div
              key={tree.id}
              // `h-9` on both this and the New tree button, so the row lines up whatever the
              // badges inside happen to make the pill: its own height came from its content.
              className={[
                "flex h-9 items-center gap-1 rounded-md border px-2",
                active ? "border-primary bg-surface-hover" : "border-border bg-surface",
              ].join(" ")}
            >
              <button
                type="button"
                onClick={() => onSelect(tree.id)}
                aria-current={active ? "true" : undefined}
                className="flex items-center gap-2 text-sm"
              >
                <span className={active ? "font-medium" : "text-text-muted"}>{tree.title}</span>
                {tree.active ? (
                  <Badge tone="success" size="sm">
                    Active
                  </Badge>
                ) : null}
                {tree.published_version ? (
                  <Badge tone="accent" size="sm">
                    v{tree.published_version}
                  </Badge>
                ) : (
                  <Badge tone="outline" size="sm">
                    Not published
                  </Badge>
                )}
                {tree.draft_id ? (
                  <Badge tone="outline" size="sm">
                    Draft
                  </Badge>
                ) : null}
              </button>
              {readOnly ? null : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1"
                      aria-label={`What to do with ${tree.title}`}
                    >
                      ⋯
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {tree.active ? (
                      <DropdownMenuItem onSelect={() => onActivate(tree, false)}>
                        Stop running this tree
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        disabled={!tree.published_version}
                        title={
                          tree.published_version
                            ? undefined
                            : "Publish a version of this tree before running it."
                        }
                        onSelect={() => tree.published_version && onActivate(tree, true)}
                      >
                        <Play className="mr-2 size-3.5" />
                        Run this tree
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onSelect={() => onDuplicate(tree)}>
                      <Copy className="mr-2 size-3.5" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={index === 0}
                      onSelect={() => onMove(tree, -1)}
                    >
                      <ArrowUp className="mr-2 size-3.5" />
                      Try earlier
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={index === trees.length - 1}
                      onSelect={() => onMove(tree, 1)}
                    >
                      <ArrowDown className="mr-2 size-3.5" />
                      Try later
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => onArchive(tree)}>
                      <Archive className="mr-2 size-3.5" />
                      Archive
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })}
        {readOnly ? null : (
          <Button variant="outline" size="sm" className="h-9" onClick={onAdd}>
            <Plus className="mr-1 size-3.5" />
            New tree
          </Button>
        )}
      </div>
      {status}
      {warnings.map((warning) => (
        <Callout key={warning} tone="warning">
          {warning}
        </Callout>
      ))}
    </div>
  );
}
